import { useEffect, useState } from 'react';
import { api, type OwnerDupGroup, type OwnerDupMember, type OwnerMergeLog } from '../lib/api';
import { ErrorBox } from './DataTable';

/**
 * 외부 차주 중복 정리(2026-10-17 way 「외부 차주도 같은 방식으로」) — 거래처 「중복 정리」(CustDupPanel)와 같은 쓰임.
 *
 * 관광사는 사업자번호, 개인은 계좌가 같은 외부 차주 묶음. 대표를 골라 합치면 차량·편성·배차·정산 기록이 대표로 옮겨 가고
 * 합친 차주는 숨겨진다(금액 그대로). 이름까지 같은 묶음은 스크립트(owner-merge.mjs)가 먼저 합친다.
 * 대표에 같은 달 이월잔액·정산 대상 줄이 이미 있으면 그 줄은 원래 차주에 남긴다(서버 ownerMerge.ts 주석).
 */

const SHOW = 30;

/** 대표 기본값 = 운행 편성(종료 안 됨) 많은 차주 → 배정 많은 차주 → 번호 작은 것(스크립트와 같은 기준). */
function pickRep(ms: OwnerDupMember[]): number {
  return [...ms].sort((a, b) => b.n_live_alloc - a.n_live_alloc || b.n_sch - a.n_sch || a.owner_id - b.owner_id)[0]!.owner_id;
}

const keyFmt = (g: OwnerDupGroup) => (g.kind === '관광사' ? `${g.key.slice(0, 3)}-${g.key.slice(3, 5)}-${g.key.slice(5)}` : `계좌 ${g.key}`);

export default function OwnerDupPanel({ canWrite, onClose, onChanged }: {
  canWrite: boolean; onClose: () => void; onChanged: (msg: string) => void;
}) {
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState<OwnerDupGroup[] | null>(null);
  const [recent, setRecent] = useState<OwnerMergeLog[]>([]);
  const [limit, setLimit] = useState(SHOW);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setGroups(null);
    setError(null);
    api.listOwnerDupGroups({ q: q || undefined })
      .then((r) => { if (live) { setGroups(r.groups); setRecent(r.recent); } })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [q, nonce]);

  function done(msg: string) {
    setNotice(msg);
    onChanged(msg);
    setNonce((n) => n + 1);
  }

  async function undo(l: OwnerMergeLog) {
    setError(null);
    try {
      await api.undoOwnerMerge({ logId: Number(l.id) });
      done(`되돌렸습니다 — 「${l.from_name ?? ''}」(차주 ${l.from_owner_id})가 다시 보입니다.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const members = groups?.reduce((s, g) => s + g.members.length, 0) ?? 0;

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">외부 차주 중복 정리</h2>
        <form className="ml-auto flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setLimit(SHOW); }}>
          <input className="field w-56" placeholder="차주명 · 사업자번호 · 계좌" value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-ghost" type="submit">검색</button>
        </form>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
      <p className="text-sm text-ink-soft">
        관광사는 사업자번호, 개인은 계좌가 같은 외부 차주입니다{groups ? ` — ${groups.length}묶음 · ${members}명` : ''}.
        같은 차주면 <b>대표</b>를 고르고 합치세요. 차량·운행편성·배차·정산 기록이 대표로 옮겨 가고, 합친 차주는 숨겨집니다(금액은 그대로).
        다른 사람·다른 관광사면 체크를 풀어 두세요. 합친 뒤 기사·차량 이름(관광사_이름)이 대표 이름과 다르면 기준정보에서 고쳐 주세요.
      </p>

      {notice && (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <span>{notice}</span>
          <button className="ml-auto text-xs underline" onClick={() => setNotice(null)}>닫기</button>
        </div>
      )}
      {error && <ErrorBox message={error} />}

      <div className="max-h-[calc(100vh-18rem)] space-y-3 overflow-auto pr-1">
        {!groups && !error && <p className="text-sm text-ink-soft">불러오는 중…</p>}
        {groups?.length === 0 && <p className="text-sm text-ink-soft">정리할 묶음이 없습니다.</p>}
        {groups?.slice(0, limit).map((g) => (
          <GroupCard key={`${g.kind}|${g.key}|${g.members.map((m) => m.owner_id).join(',')}`} g={g} canWrite={canWrite} onMerged={done} onError={setError} />
        ))}
        {groups && groups.length > limit && (
          <button className="btn-ghost w-full" onClick={() => setLimit((n) => n + SHOW)}>더 보기 ({groups.length - limit}묶음 남음)</button>
        )}

        {recent.length > 0 && (
          <div className="rounded-lg border border-line p-3">
            <h3 className="mb-2 text-sm font-bold">최근 병합(이 화면)</h3>
            <table className="w-full text-sm">
              <tbody>
                {recent.map((l) => (
                  <tr key={l.id} className="border-t border-line">
                    <td className="py-1 pr-2 text-ink-soft">{l.created_at.slice(0, 16)}</td>
                    <td className="py-1 pr-2">{l.from_name ?? ''} <span className="text-ink-soft">({l.from_owner_id})</span> → {l.to_name ?? ''} <span className="text-ink-soft">({l.to_owner_id})</span></td>
                    <td className="py-1 pr-2 text-ink-soft">{Object.keys(l.kept ?? {}).length ? '같은 달 줄 일부 남김' : ''}</td>
                    <td className="py-1 pr-2 text-ink-soft">{l.created_by?.split('@')[0]}</td>
                    <td className="py-1 text-right">
                      {l.is_undone
                        ? <span className="text-xs text-ink-soft">되돌림</span>
                        : canWrite && <button className="btn-ghost py-0.5 text-xs" onClick={() => void undo(l)}>되돌리기</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function GroupCard({ g, canWrite, onMerged, onError }: {
  g: OwnerDupGroup; canWrite: boolean; onMerged: (msg: string) => void; onError: (m: string | null) => void;
}) {
  const [rep, setRep] = useState(() => pickRep(g.members));
  const [checked, setChecked] = useState<Set<number>>(() => new Set(g.members.map((m) => m.owner_id)));
  const [arm, setArm] = useState(false);
  const [busy, setBusy] = useState(false);
  const repName = g.members.find((m) => m.owner_id === rep)?.owner_name ?? '';
  const from = g.members.filter((m) => m.owner_id !== rep && checked.has(m.owner_id));

  async function merge() {
    if (!arm) { setArm(true); return; }
    setBusy(true);
    onError(null);
    try {
      const r = await api.mergeOwners({ toOwnerId: rep, fromOwnerIds: from.map((m) => m.owner_id) });
      const kept = Object.values(r.kept).reduce((s, n) => s + n, 0);
      onMerged(`「${repName}」(차주 ${rep})로 ${r.merged}명을 합쳤습니다${kept ? ` — 대표에 같은 달 줄이 있는 ${kept}줄은 원래 차주에 남겼습니다` : ''}.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
      setArm(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-line">
      <div className="flex flex-wrap items-center gap-3 border-b border-line bg-canvas px-3 py-2 text-sm">
        <span className="rounded bg-sky-50 px-1.5 text-xs text-sky-800">{g.kind}</span>
        <b>{keyFmt(g)}</b>
        <span className="text-ink-soft">{g.members.length}명 · 운행 편성 {g.n_live_alloc} · 배정 {g.n_sch.toLocaleString()}</span>
        {canWrite && (
          <button className={`ml-auto ${arm ? 'btn-primary bg-red-600 hover:bg-red-700' : 'btn-primary'}`} disabled={busy || !from.length}
                  onClick={() => void merge()} onBlur={() => setArm(false)}>
            {busy ? '합치는 중…' : arm ? `한 번 더 누르면 ${from.length}명을 합칩니다` : `${from.length}명을 대표로 합치기`}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="text-left text-xs text-ink-soft">
            <tr>
              <th className="px-2 py-1">대표</th>
              <th className="px-2 py-1">합치기</th>
              <th className="px-2 py-1">NO</th>
              <th className="px-2 py-1">차주명</th>
              <th className="px-2 py-1">휴대전화</th>
              <th className="px-2 py-1">{g.kind === '관광사' ? '이메일' : '은행'}</th>
              <th className="px-2 py-1 text-right">운행 편성</th>
              <th className="px-2 py-1 text-right">차량</th>
              <th className="px-2 py-1 text-right">배정</th>
              <th className="px-2 py-1">최근 명세</th>
            </tr>
          </thead>
          <tbody>
            {g.members.map((m) => {
              const isRep = m.owner_id === rep;
              const on = !isRep && checked.has(m.owner_id);
              return (
                <tr key={m.owner_id} className={`border-t border-line ${isRep ? 'bg-brand-50' : on ? '' : 'text-ink-soft'}`}>
                  <td className="px-2 py-1">
                    <input type="radio" name={`rep-${g.kind}-${g.key}`} checked={isRep} disabled={!canWrite} aria-label="대표"
                           onChange={() => { setRep(m.owner_id); setArm(false); }} />
                  </td>
                  <td className="px-2 py-1">
                    {!isRep && (
                      <input type="checkbox" checked={on} disabled={!canWrite} aria-label="합치기"
                             onChange={(e) => { setArm(false); setChecked((cur) => { const n = new Set(cur); if (e.target.checked) n.add(m.owner_id); else n.delete(m.owner_id); return n; }); }} />
                    )}
                  </td>
                  <td className="px-2 py-1 text-ink-soft">{m.owner_id}</td>
                  <td className="px-2 py-1 font-medium">{m.owner_name}{isRep && <span className="ml-2 rounded bg-brand-100 px-1.5 text-xs text-brand-700">대표</span>}</td>
                  <td className="px-2 py-1">{m.mobile_phone || '—'}</td>
                  <td className="px-2 py-1">{(g.kind === '관광사' ? m.email : m.bank_name) || '—'}</td>
                  <td className="px-2 py-1 text-right">{m.n_live_alloc}</td>
                  <td className="px-2 py-1 text-right">{m.n_car}</td>
                  <td className="px-2 py-1 text-right">{m.n_sch.toLocaleString()}</td>
                  <td className="px-2 py-1">{m.last_salary ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
